// src/pages/dashboard/admin/Dashboard.jsx
import { useState, useEffect } from 'react';
import api from '../../../lib/api';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { 
  Users, Shield, ShieldCheck, TrendingUp, Ban, 
  AlertCircle, MessageSquare, Calendar, CheckCircle, XCircle 
} from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(res => setStats(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <LoadingSpinner size="lg" />;

  const { daily_signups_7d, disputes, complaints } = stats;

  return (
    <div className="max-w-7xl mx-auto space-y-10 py-8">
      <h1 className="text-5xl font-bold text-center">Admin Control Center</h1>

      {/* Main KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-3xl p-8 text-center shadow-xl">
          <Users size={50} className="mx-auto text-primary mb-4" />
          <p className="text-4xl font-black">{stats.total_users.toLocaleString()}</p>
          <p className="text-gray-600">Total Users</p>
        </div>

        <div className="bg-white rounded-3xl p-8 text-center shadow-xl">
          <Shield size={50} className="mx-auto text-green-600 mb-4" />
          <p className="text-4xl font-black">{stats.vetted_nannies}</p>
          <p className="text-gray-600">Vetted Nannies</p>
        </div>

        <div className="bg-white rounded-3xl p-8 text-center shadow-xl">
          <ShieldCheck size={50} className="mx-auto text-green-600 mb-4" />
          <p className="text-4xl font-black">{stats.kyc_complete}</p>
          <p className="text-gray-600">KYC Complete</p>
        </div>

        <div className="bg-white rounded-3xl p-8 text-center shadow-xl">
          <TrendingUp size={50} className="mx-auto text-blue-600 mb-4" />
          <p className="text-4xl font-black">{stats.active_contracts}</p>
          <p className="text-gray-600">Active Contracts</p>
        </div>

        <div className="bg-red-50 rounded-3xl p-8 text-center shadow-xl border-2 border-red-200">
          <Ban size={50} className="mx-auto text-red-600 mb-4" />
          <p className="text-4xl font-black">{stats.bans_today}</p>
          <p className="text-red-700 font-bold">Bans Today</p>
        </div>
      </div>

      {/* Disputes & Complaints */}
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-3xl p-10 shadow-2xl">
          <AlertCircle size={60} className="mb-6" />
          <p className="text-6xl font-black">{disputes.open}</p>
          <p className="text-2xl mt-4">Open Disputes</p>
          <div className="mt-4 space-y-2 text-lg opacity-90">
            <p>{disputes.resolved} Resolved</p>
            <p>{disputes.dismissed} Dismissed</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-3xl p-10 shadow-2xl">
          <MessageSquare size={60} className="mb-6" />
          <p className="text-6xl font-black">{complaints.pending}</p>
          <p className="text-2xl mt-4">Pending Complaints</p>
          <div className="mt-4 space-y-2 text-lg opacity-90">
            <p>{complaints.resolved} Resolved</p>
            <p>{complaints.dismissed} Dismissed</p>
          </div>
        </div>
      </div>

      {/* 30-Day Growth */}
      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white rounded-3xl shadow-2xl p-10 text-center">
          <Users size={60} className="mx-auto text-green-600 mb-6" />
          <p className="text-6xl font-black text-green-600">+{stats.new_users_30d}</p>
          <p className="text-2xl text-gray-700 mt-4">New Users (30d)</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-10 text-center">
          <Users size={60} className="mx-auto text-blue-600 mb-6" />
          <p className="text-6xl font-black text-blue-600">+{stats.new_nannies_30d}</p>
          <p className="text-2xl text-gray-700 mt-4">New Nannies (30d)</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-3xl shadow-2xl p-10 text-center">
          <TrendingUp size={60} className="mb-6" />
          <p className="text-7xl font-black">
            {stats.growth_rate_30d >= 999 ? 'New!' : `+${stats.growth_rate_30d}%`}
          </p>
          <p className="text-2xl mt-4">30-Day Growth</p>
        </div>
      </div>

      {/* Calendar Growth */}
      <div className="bg-white rounded-3xl shadow-2xl p-10">
        <h2 className="text-4xl font-bold text-center mb-10">User Growth (Last 7 Days)</h2>
        <div className="grid grid-cols-7 gap-6">
          {daily_signups_7d.map((day, i) => {
            const max = Math.max(...daily_signups_7d.map(d => d.count));
            const height = max === 0 ? 10 : (day.count / max) * 100;

            return (
              <div key={i} className="text-center">
                <p className="text-lg font-semibold text-gray-600 mb-4">{day.date}</p>
                <div className="relative h-48 flex items-end justify-center">
                  <div 
                    className="w-20 bg-gradient-to-t from-primary to-primary/40 rounded-t-3xl transition-all duration-700 shadow-lg hover:shadow-2xl"
                    style={{ height: `${height}%` }}
                  >
                    <span className={`absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-sm font-bold shadow-lg
                      ${day.count > 0 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {day.count}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Motivational */}
      <div className="text-center py-20">
        <h2 className="text-4xl font-bold mb-8">AuthWorka is Growing</h2>
        <p className="text-2xl text-gray-600">You are building the future of trust in Nigeria.</p>
      </div>
    </div>
  );
}